import { Button as PrimeButton } from "primereact/button";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AdminDashboard as AdminDashboardType } from "../service/dashboardService";
import { dashboardService } from "../service/dashboardService";
import "./AdminDashboard.enterprise.css";
import PageHeader from "./PageHeader";
import SEO from "./SEO";

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AdminDashboardType | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);

  const navigate = useNavigate();

  const userFullName =
    useSelector((state: any) => state.auth.userFullName) || "Administrator";
  const userRole = useSelector((state: any) => state.auth.userRole) || "";

  // Professional enterprise color palette
  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4"];

  const fetchDashboardData = async () => {
    try {
      setError("");
      console.log("Fetching dashboard data...");
      const data = await dashboardService.getAdminDashboard();
      console.log("Dashboard data received:", data);
      console.log("Stats data:", data.stats);
      console.log("Top RMCs data:", data.topRMCs);
      console.log("Recent Activities data:", data.recentActivities);
      console.log("Monthly Stats data:", data.monthlyStats);
      console.log("Data types check:", {
        stats: data.stats,
        topRMCs: data.topRMCs,
        recentActivities: data.recentActivities,
        monthlyStats: data.monthlyStats,
      });
      setDashboardData(data);
    } catch (err: any) {
      setError(err.message || "Failed to load dashboard data");
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Manual refresh handler
  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // Custom tooltip formatter for Top RMCs chart
  const renderTopRMCTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const fullName = data?.originalName || data?.name || "Unknown RMC";
      const safeCityName = data?.cityName || "Unknown City";
      const safeTotalRooms = data?.totalRooms || 0;

      return (
        <div
          className="bg-white p-3 border rounded shadow"
          style={{ maxWidth: "250px" }}
        >
          <p className="fw-bold mb-2" style={{ fontSize: "0.9rem" }}>
            {`RMC ${label}: ${fullName}`}
          </p>
          <p className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
            {safeCityName}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              className="mb-1"
              style={{ color: entry.color, fontSize: "0.85rem" }}
            >
              {`${entry.name || "Unknown"}: ${entry.value || 0}`}
            </p>
          ))}
          <p className="text-muted small mb-0" style={{ fontSize: "0.75rem" }}>
            Total Rooms: {safeTotalRooms}
          </p>
        </div>
      );
    }
    return null;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString();

  const getStatIcon = (statType: string) => {
    const iconMap: { [key: string]: any } = {
      rmcs: Icon.Building,
      clients: Icon.People,
      users: Icon.PersonCircle,
      properties: Icon.HouseDoor,
      rooms: Icon.DoorOpen,
      bookings: Icon.CalendarCheck,
      amenities: Icon.Star,
      facilities: Icon.Gear,
      locations: Icon.GeoAlt,
    };
    return iconMap[statType] || Icon.BarChart;
  };

  // Map an activity icon name (string from API) to a react-bootstrap-icons component
  const getIconComponent = (iconName: string) => {
    const activityIconMap: { [key: string]: React.ReactNode } = {
      building: <Icon.Building />,
      user: <Icon.PersonCircle />,
      client: <Icon.People />,
      property: <Icon.HouseDoor />,
      room: <Icon.DoorOpen />,
      booking: <Icon.CalendarCheck />,
      amenity: <Icon.Star />,
      facility: <Icon.Gear />,
      location: <Icon.GeoAlt />,
      create: <Icon.PlusCircle />,
      update: <Icon.PencilSquare />,
      delete: <Icon.Trash />,
    };

    // Safe check for iconName
    const safeIconName = iconName?.toLowerCase?.() || "";
    return activityIconMap[safeIconName] || <Icon.Activity />;
  };

  // Navigation handlers for dashboard tiles
  const handleRMCClick = () => navigate("/admin/rmc");
  const handleClientsClick = () => navigate("/admin/clientsmaster");
  const handleUsersClick = () => navigate("/admin/users");
  const handlePropertiesClick = () => navigate("/admin/property");
  const handleRoomsClick = () => navigate("/admin/rmc");
  const handleBookingsClick = () => navigate("/admin/reservation");

  // Handle RMC chart bar click for drill-down
  const handleRMCBarClick = (data: any) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const rmcData = data.activePayload[0].payload;
      console.log("RMC clicked:", rmcData);
      // Navigate to specific RMC dashboard or details page
      navigate(`/admin/rmc/${rmcData.id}`);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center text-center"
        style={{ height: "55vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <div className="mt-3 fw-semibold text-muted">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Icon.ExclamationTriangle className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return null;
  }

  // Add defensive checks for API response structure
  console.log("Dashboard data structure:", dashboardData);
  console.log("Dashboard data keys:", Object.keys(dashboardData));

  const Stats =
    (dashboardData as any).stats || (dashboardData as any).stats || {};
  const RecentActivities =
    (dashboardData as any).RecentActivities ||
    (dashboardData as any).recentActivities ||
    [];
  const TopRMCs =
    (dashboardData as any).TopRMCs || (dashboardData as any).topRMCs || [];
  const MonthlyStats =
    (dashboardData as any).MonthlyStats ||
    (dashboardData as any).monthlyStats ||
    [];

  console.log("Extracted data:", {
    Stats,
    RecentActivities,
    TopRMCs,
    MonthlyStats,
  });

  // Ensure arrays are properly initialized
  const safeMonthlyStats = Array.isArray(MonthlyStats) ? MonthlyStats : [];
  const safeRecentActivities = Array.isArray(RecentActivities)
    ? RecentActivities
    : [];
  const safeTopRMCs = Array.isArray(TopRMCs) ? TopRMCs : [];

  // Ensure Stats has default values to prevent undefined errors
  const safeStats = {
    TotalRMCs: Stats.totalRMCs || 0,
    ActiveRMCs: Stats.activeRMCs || 0,
    TotalClients: Stats.totalClients || 0,
    ActiveClients: Stats.activeClients || 0,
    TotalUsers: Stats.totalUsers || 0,
    ActiveUsers: Stats.activeUsers || 0,
    TotalProperties: Stats.totalProperties || 0,
    ActiveProperties: Stats.activeProperties || 0,
    TotalRooms: Stats.totalRooms || 0,
    AvailableRooms: Stats.availableRooms || 0,
    TotalBookings: Stats.totalBookings || 0,
    ActiveBookings: Stats.activeBookings || 0,
  };

  // Debug logging
  console.log("Rendering dashboard with:", {
    totalRMCs: safeTopRMCs.length,
    totalActivities: safeRecentActivities.length,
    totalMonthlyStats: safeMonthlyStats.length,
    topRMCsData: safeTopRMCs,
    activitiesData: safeRecentActivities,
    monthlyStatsData: safeMonthlyStats,
    statsData: safeStats,
    firstActivity: safeRecentActivities[0],
    firstRMC: safeTopRMCs[0],
    firstMonthlyData: safeMonthlyStats[0],
  });

  return (
    <div className="content">
      <SEO
        title="Admin Dashboard"
        noIndex
        description="Administrative dashboard"
      />
      <PageHeader
        title="Admin Dashboard"
        showBackButton={false}
        beforeTitle={
          <PrimeButton
            icon={`pi pi-refresh ${refreshing ? "pi-spin" : ""}`}
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            disabled={refreshing}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Admin Dashboard"
          />
        }
      />
      <div className="content-body">
        <div className="container" style={{ maxWidth: "1400px" }}>
          <div className="d-flex flex-column flex-sm-row align-items-sm-end align-items-start justify-content-between mb-3">
            <div>
              <p className="text-muted mb-1">Welcome back, {userFullName}!</p>
              <h5
                className="fw-semibold mb-0"
                style={{ letterSpacing: ".5px" }}
              >
                System Overview
              </h5>
            </div>
          </div>

          {/* Main Stats Cards */}
          <Row className="mb-4 g-3">
            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleRMCClick}
                title="Click to view RMCs"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-primary text-white me-3">
                    <Icon.Building size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-primary">{safeStats.TotalRMCs}</h4>
                    <div className="soft-muted">RMCs</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.CheckCircleFill /> {safeStats.ActiveRMCs} active
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleClientsClick}
                title="Click to view Clients"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-success text-white me-3">
                    <Icon.People size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-success">
                      {safeStats.TotalClients}
                    </h4>
                    <div className="soft-muted">Clients</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.PersonCheckFill /> {safeStats.ActiveClients}{" "}
                        active
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleUsersClick}
                title="Click to view Users"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-info text-white me-3">
                    <Icon.PersonCircle size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-info">{safeStats.TotalUsers}</h4>
                    <div className="soft-muted">Users</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.Dot /> {safeStats.ActiveUsers} active
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handlePropertiesClick}
                title="Click to view Properties"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-warning text-white me-3">
                    <Icon.HouseDoor size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-warning">
                      {safeStats.TotalProperties}
                    </h4>
                    <div className="soft-muted">Properties</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.HouseCheckFill /> {safeStats.ActiveProperties}{" "}
                        active
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleRoomsClick}
                title="Click to view Rooms (via Properties)"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-secondary text-white me-3">
                    <Icon.DoorOpen size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-secondary">
                      {safeStats.TotalRooms}
                    </h4>
                    <div className="soft-muted">Rooms</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.DoorClosedFill /> {safeStats.AvailableRooms} avail
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={2} lg={3} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleBookingsClick}
                title="Click to view Bookings/Reservations"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-danger text-white me-3">
                    <Icon.CalendarCheck size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-danger">
                      {safeStats.TotalBookings}
                    </h4>
                    <div className="soft-muted">Bookings</div>
                    <div className="delta-badges">
                      <span className="delta-chip delta-positive">
                        <Icon.Calendar2CheckFill /> {safeStats.ActiveBookings}{" "}
                        active
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="mb-4 g-4">
            {/* Monthly Growth Trends */}
            <Col lg={8} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.GraphUp className="me-2" />
                    Monthly Growth Trends
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={safeMonthlyStats.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="newUsers"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="New Users"
                      />
                      <Line
                        type="monotone"
                        dataKey="newClients"
                        stroke="#10b981"
                        strokeWidth={2}
                        name="New Clients"
                      />
                      <Line
                        type="monotone"
                        dataKey="newProperties"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="New Properties"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            {/* System Distribution Pie Chart */}
            <Col lg={4} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.PieChart className="me-2" />
                    System Distribution
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Properties",
                            value: safeStats.TotalProperties,
                            color: "#3b82f6",
                          },
                          {
                            name: "Clients",
                            value: safeStats.TotalClients,
                            color: "#10b981",
                          },
                          {
                            name: "Users",
                            value: safeStats.TotalUsers,
                            color: "#f59e0b",
                          },
                          {
                            name: "RMCs",
                            value: safeStats.TotalRMCs,
                            color: "#ef4444",
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label
                      >
                        {[
                          {
                            name: "Properties",
                            value: safeStats.TotalProperties,
                            color: "#3b82f6",
                          },
                          {
                            name: "Clients",
                            value: safeStats.TotalClients,
                            color: "#10b981",
                          },
                          {
                            name: "Users",
                            value: safeStats.TotalUsers,
                            color: "#f59e0b",
                          },
                          {
                            name: "RMCs",
                            value: safeStats.TotalRMCs,
                            color: "#ef4444",
                          },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Revenue and Performance Charts */}
          <Row className="mb-4 g-4">
            {/* Monthly Revenue Chart */}
            <Col lg={6} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.CurrencyDollar className="me-2" />
                    Monthly Revenue
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={safeMonthlyStats.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            {/* Top RMCs Performance Bar Chart */}
            <Col lg={6} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.BarChart className="me-2" />
                    Top RMCs Performance
                  </h5>
                </Card.Header>
                <Card.Body>
                  {safeTopRMCs.length === 0 ? (
                    <div
                      className="d-flex flex-column justify-content-center align-items-center text-center"
                      style={{ height: "280px" }}
                    >
                      <Icon.InfoCircle size={24} className="text-muted mb-2" />
                      <div className="text-muted">
                        No RMC performance data available
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={safeTopRMCs.slice(0, 5).map((rmc, index) => ({
                          ...rmc,
                          displayName: (index + 1).toString(),
                          originalName: rmc.name,
                        }))}
                        onClick={handleRMCBarClick}
                        style={{ cursor: "pointer" }}
                        margin={{ top: 10, right: 10, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="displayName"
                          angle={0}
                          textAnchor="middle"
                          height={50}
                          interval={0}
                          fontSize={14}
                          tick={{ fontSize: 14 }}
                        />
                        <YAxis />
                        <Tooltip content={renderTopRMCTooltip} />
                        <Legend />
                        <Bar
                          dataKey="totalClients"
                          fill="#3b82f6"
                          name="Clients"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar
                          dataKey="totalProperties"
                          fill="#10b981"
                          name="Properties"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar
                          dataKey="activeBookings"
                          fill="#f59e0b"
                          name="Active Bookings"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Details Row */}
          <Row className="mb-4 g-4">
            {/* Recent Activities */}
            <Col lg={6} className="mb-3">
              <Card className="h-100 chart-card gradient">
                <Card.Header className="widget-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Icon.Clock className="me-2" />
                    Recent Activities
                  </h5>
                  <Badge bg="primary">{safeRecentActivities.length}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="activity-list">
                    {safeRecentActivities.length === 0 ? (
                      <div className="d-flex flex-column justify-content-center align-items-center text-center p-4">
                        <Icon.InfoCircle
                          size={24}
                          className="text-muted mb-2"
                        />
                        <div className="text-muted">
                          No recent activities available
                        </div>
                        <small className="text-muted">
                          Activities will appear here once there's data
                        </small>
                      </div>
                    ) : (
                      safeRecentActivities.map((activity, index) => {
                        // Safe fallback values for activity properties - using camelCase from API
                        const safeActivity = {
                          Id: activity?.id || `activity-${index}`,
                          Type: activity?.type || "Unknown",
                          Action: activity?.action || "Unknown",
                          EntityName: activity?.entityName || "Unknown Entity",
                          UserName: activity?.userName || "Unknown User",
                          CreatedDate:
                            activity?.createdDate || new Date().toISOString(),
                          Icon: activity?.icon || "Activity",
                          Color: activity?.color || "primary",
                        };

                        return (
                          <div
                            key={safeActivity.Id}
                            className="activity-item p-3 border-0"
                          >
                            <span className="timeline-dot" />
                            <div className="d-flex align-items-start">
                              <div
                                className={`activity-icon me-3 text-${safeActivity.Color}`}
                              >
                                {getIconComponent(safeActivity.Icon)}
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <div
                                    className="fw-semibold"
                                    style={{ fontSize: ".85rem" }}
                                  >
                                    {safeActivity.Action}{" "}
                                    {safeActivity.Type.toLowerCase()}
                                  </div>
                                  <small
                                    className="text-muted"
                                    style={{ fontSize: ".7rem" }}
                                  >
                                    {formatDate(safeActivity.CreatedDate)}
                                  </small>
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: ".7rem" }}
                                >
                                  {safeActivity.EntityName} by{" "}
                                  {safeActivity.UserName}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Top RMCs */}
            <Col lg={6} className="mb-3">
              <Card className="h-100 chart-card gradient">
                <Card.Header className="widget-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Icon.Trophy className="me-2" />
                    Top Performing RMCs
                  </h5>
                  <Badge bg="success">{safeTopRMCs.length}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  {safeTopRMCs.length === 0 ? (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center p-4">
                      <Icon.InfoCircle size={24} className="text-muted mb-2" />
                      <div className="text-muted">
                        No top RMCs data available
                      </div>
                      <small className="text-muted">
                        RMC performance data will appear here once available
                      </small>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0 top-rmc-table">
                        <thead>
                          <tr>
                            <th>RMC</th>
                            <th>Clients</th>
                            <th>Properties</th>
                            <th>Rooms</th>
                            <th>Bookings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safeTopRMCs.map((rmc, index) => {
                            // Safe fallback values for RMC properties - using camelCase from API
                            const safeRMC = {
                              Id: rmc?.id || `rmc-${index}`,
                              Name: rmc?.name || "Unknown RMC",
                              CityName: rmc?.cityName || "Unknown City",
                              TotalClients: rmc?.totalClients || 0,
                              TotalProperties: rmc?.totalProperties || 0,
                              TotalRooms: rmc?.totalRooms || 0,
                              ActiveBookings: rmc?.activeBookings || 0,
                            };

                            const capacityPct = safeRMC.TotalRooms
                              ? Math.min(
                                  100,
                                  Math.round(
                                    (safeRMC.ActiveBookings /
                                      safeRMC.TotalRooms) *
                                      100
                                  )
                                )
                              : 0;

                            return (
                              <tr key={safeRMC.Id}>
                                <td style={{ minWidth: 160 }}>
                                  <div
                                    className="fw-semibold"
                                    style={{ fontSize: ".8rem" }}
                                  >
                                    {safeRMC.Name}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: ".65rem" }}
                                  >
                                    {safeRMC.CityName}
                                  </div>
                                  <div className="progress-thin mt-1">
                                    <span
                                      style={{ width: capacityPct + "%" }}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <span className="metric-badge clients">
                                    <Icon.PeopleFill size={10} />{" "}
                                    {safeRMC.TotalClients}
                                  </span>
                                </td>
                                <td>
                                  <span className="metric-badge properties">
                                    <Icon.HouseFill size={10} />{" "}
                                    {safeRMC.TotalProperties}
                                  </span>
                                </td>
                                <td>
                                  <span className="metric-badge rooms">
                                    <Icon.DoorClosedFill size={10} />{" "}
                                    {safeRMC.TotalRooms}
                                  </span>
                                </td>
                                <td>
                                  <span className="metric-badge bookings">
                                    <Icon.Calendar2CheckFill size={10} />{" "}
                                    {safeRMC.ActiveBookings}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
